import { useCallback, useEffect, useState } from 'react';
import type {
  CockpitSettings,
  DebtInstrument,
  OwnerDistribution,
} from '../types/finances';
import { DEFAULT_COCKPIT_SETTINGS } from '../types/finances';
import {
  addDebtInstrument,
  addOwnerDistribution,
  deleteDebtInstrument,
  deleteOwnerDistribution,
  saveCockpitSettings,
  subscribeCockpitSettings,
  subscribeDebtInstruments,
  subscribeOwnerDistributions,
  updateDebtInstrument,
} from '../utils/cockpitService';

export function useCockpitWorkspace(actorUserId?: string) {
  const [settings, setSettings] = useState<CockpitSettings>(DEFAULT_COCKPIT_SETTINGS);
  const [draws, setDraws] = useState<OwnerDistribution[]>([]);
  const [debts, setDebts] = useState<DebtInstrument[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => subscribeCockpitSettings(setSettings, (err) => setError(err.message)), []);
  useEffect(() => subscribeOwnerDistributions(setDraws, (err) => setError(err.message)), []);
  useEffect(() => subscribeDebtInstruments(setDebts, (err) => setError(err.message)), []);

  const persistSettings = useCallback(async (next: CockpitSettings) => {
    if (!actorUserId) return;
    setBusy(true);
    setError(null);
    try {
      await saveCockpitSettings(next, actorUserId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save cockpit settings.');
    } finally {
      setBusy(false);
    }
  }, [actorUserId]);

  const addDraw = useCallback(async (draw: Omit<OwnerDistribution, 'id' | 'createdAt' | 'createdBy'>) => {
    if (!actorUserId) return;
    setBusy(true);
    setError(null);
    try {
      await addOwnerDistribution({ ...draw, createdBy: actorUserId });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add distribution.');
    } finally {
      setBusy(false);
    }
  }, [actorUserId]);

  const removeDraw = useCallback(async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await deleteOwnerDistribution(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete distribution.');
    } finally {
      setBusy(false);
    }
  }, []);

  const addDebt = useCallback(async (row: Omit<DebtInstrument, 'id' | 'createdAt' | 'updatedAt'>) => {
    setBusy(true);
    setError(null);
    try {
      await addDebtInstrument(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add loan.');
    } finally {
      setBusy(false);
    }
  }, []);

  const patchDebt = useCallback(async (
    id: string,
    patch: Partial<Omit<DebtInstrument, 'id' | 'createdAt'>>,
  ) => {
    setBusy(true);
    setError(null);
    try {
      await updateDebtInstrument(id, patch);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update loan.');
    } finally {
      setBusy(false);
    }
  }, []);

  const removeDebt = useCallback(async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await deleteDebtInstrument(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete loan.');
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    settings,
    draws,
    debts,
    busy,
    error,
    persistSettings,
    addDraw,
    removeDraw,
    addDebt,
    patchDebt,
    removeDebt,
  };
}
